import { useId, useState } from 'react'
import { Sheet } from '../components/Sheet'
import { NameField, maxNameLength } from '../components/NameField'
import { plural } from '../lib/format'
import { useSession } from '../session/context'

/**
 * Ronde 10B en 11B — "Naam aanpassen". Op mobiel een sheet, op desktop een
 * dialoog; dezelfde inhoud.
 *
 * Het formulier hangt aan een `key` die omslaat met `open`, dus elke keer
 * beginnen het veld en de foutmelding vers. Dat scheelt een effect dat state
 * terugzet en een render-ronde waarin het oude nog even staat.
 *
 * Die `key` zit hier en niet op het paneel zelf, want daarbuiten zou hij bij
 * het sluiten de hele `Sheet` opnieuw opbouwen — en dan is er niets meer om uit
 * beeld te schuiven.
 */
export function EditNamePanel({
  open,
  uploadCount,
  onClose,
}: {
  open: boolean
  uploadCount: number
  onClose: () => void
}) {
  const headingId = useId()

  return (
    <Sheet open={open} onClose={onClose} labelledBy={headingId}>
      <NameForm
        key={open ? 'open' : 'dicht'}
        headingId={headingId}
        uploadCount={uploadCount}
        onClose={onClose}
      />
    </Sheet>
  )
}

function NameForm({
  headingId,
  uploadCount,
  onClose,
}: {
  headingId: string
  uploadCount: number
  onClose: () => void
}) {
  const { guest, saveGuest } = useSession()

  const [name, setName] = useState(guest?.displayName ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = name.trim()

  async function save() {
    if (!trimmed || saving) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      await saveGuest(trimmed)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Opslaan lukte niet.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-5">
        <div>
          <h2 id={headingId} className="font-display text-[32px] leading-[1.15] sm:text-[36px]">
            Naam aanpassen
          </h2>
          <p className="text-ink-soft mt-2 text-sm leading-[1.5] text-pretty sm:text-[15px]">
            Dit staat bij al je foto’s, ook bij de foto’s die je eerder uploadde.
          </p>
        </div>

        <button
          type="button"
          aria-label="Sluiten"
          onClick={onClose}
          className="border-line-warm text-ink-muted hidden size-9 shrink-0 items-center justify-center rounded-full border text-[15px] sm:flex"
        >
          ✕
        </button>
      </div>

      <div className="mt-6.5 sm:mt-7.5">
        <NameField value={name} onChange={setName} id="naam-aanpassen" />

        <div className="text-ink-faint mt-2.5 flex justify-between text-[13px]">
          <span>Zichtbaar bij {plural(uploadCount, 'foto', 'foto’s')}</span>
          <span>
            {name.length} / {maxNameLength}
          </span>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-accent mt-3 text-sm">
          {error}
        </p>
      )}

      <div className="mt-7 flex gap-2.5 sm:mt-7.5 sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="border-line text-ink-muted flex h-14 shrink-0 basis-30 items-center justify-center rounded-full border text-base sm:h-13 sm:basis-auto sm:px-6"
        >
          Annuleren
        </button>

        <button
          type="button"
          onClick={() => void save()}
          disabled={!trimmed || saving}
          className="bg-accent text-accent-ink shadow-accent disabled:bg-inert disabled:text-inert-ink flex h-14 flex-1 items-center justify-center rounded-full text-base font-semibold disabled:shadow-none sm:h-13 sm:flex-none sm:px-7.5"
        >
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
      </div>
    </>
  )
}
